import { ACCESSIBLE_APPS } from '@workspace/ui/lib';
import type { JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import fetchAPI from '@/lib/fetch-api';
import { useAuthStore } from '@/stores/authStore';

export const login = async (email: string, password: string) => {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Login failed' }));

    // Handle 2FA case
    if (res.status === 428) {
      return { requires2FA: true, message: errorData.message };
    }

    throw new Error(errorData.error || 'Login failed');
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  if (
    decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYER &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE &&
    decodedToken.accessible_app !== ACCESSIBLE_APPS.CONTRACTOR
  ) {
    await logout();

    const error = new Error('Access denied: Invalid role for this application') as Error & {
      statusCode: number;
    };

    error.statusCode = 403;
    throw error;
  }

  const user: JwtUser = {
    user: {
      id: Number(decodedToken.sub),
      first_name: decodedToken.user.first_name,
      last_name: decodedToken.user.last_name,
      email: decodedToken.user.email,
    },
    permissions: decodedToken.permissions,
    roles: decodedToken.roles,
    organisations: decodedToken.organisations,
    accessible_app: decodedToken.accessible_app,
    teams: decodedToken.teams,
    has_completed_nps: decodedToken.has_completed_nps,
  };

  useAuthStore.getState().setAccessToken(data.access_token);
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setUser(user);

  return data;
};

export const logout = async () => {
  try {
    const res = await fetch('/api/auth/logout', { method: 'POST' });
    const data = await res.json();

    // Even if the API call fails, we still want to clear the local state
    useAuthStore.getState().setAccessToken(null);
    useAuthStore.getState().setAuthenticated(false);
    useAuthStore.getState().setUser(null);

    if (!res.ok) {
      console.warn('Logout API call failed:', data.error || 'Logout failed');
    }
  } catch (error) {
    // If the API call fails completely, still clear the local state
    useAuthStore.getState().setAccessToken(null);
    useAuthStore.getState().setAuthenticated(false);
    useAuthStore.getState().setUser(null);
    console.warn('Logout failed:', error);
  }
};

// Token refresh lock to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<{ success: boolean; data?: unknown }> | null = null;
let isRefreshing = false;
let lastRefreshTime = 0;
const MIN_REFRESH_INTERVAL = 5000; // Minimum 5 seconds between refreshes

export const refreshToken = async () => {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Prevent too frequent refreshes (safety check)
  const now = Date.now();
  const timeSinceLastRefresh = now - lastRefreshTime;
  if (lastRefreshTime > 0 && timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
    // Return success if we have a valid token in store
    const currentToken = useAuthStore.getState().accessToken;
    if (currentToken) {
      return { success: true, data: { access_token: currentToken } };
    }
  }

  // Double-check lock pattern: verify we're not already refreshing
  if (isRefreshing) {
    // Wait a bit and retry
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (refreshPromise) return refreshPromise;
  }

  isRefreshing = true;
  lastRefreshTime = now;

  // Start a new refresh operation
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();

      if (res.ok && data.access_token) {
        const decodedToken = jwtDecode<JwtPayload>(data.access_token);

        if (
          decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYER &&
          decodedToken.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE &&
          decodedToken.accessible_app !== ACCESSIBLE_APPS.CONTRACTOR
        ) {
          await logout();

          const error = new Error('Access denied: Invalid role for this application') as Error & {
            statusCode: number;
          };
          error.statusCode = 403;
          throw error;
        }

        const user: JwtUser = {
          user: {
            id: Number(decodedToken.sub),
            first_name: decodedToken.user.first_name,
            last_name: decodedToken.user.last_name,
            email: decodedToken.user.email,
          },
          permissions: decodedToken.permissions,
          roles: decodedToken.roles,
          organisations: decodedToken.organisations,
          accessible_app: decodedToken.accessible_app,
          teams: decodedToken.teams,
          has_completed_nps: decodedToken.has_completed_nps,
        };

        useAuthStore.getState().setAccessToken(data.access_token);
        useAuthStore.getState().setAuthenticated(true);
        useAuthStore.getState().setUser(user);

        return { success: true, data };
      }

      return { success: false };
    } catch {
      // Reset lastRefreshTime on error to allow retry
      lastRefreshTime = 0;
      return { success: false };
    } finally {
      // Clear the lock and promise
      // Use setTimeout to ensure all pending requests get the result first
      setTimeout(() => {
        isRefreshing = false;
        refreshPromise = null;
      }, 500);
    }
  })();

  return refreshPromise;
};

export const forgotPassword = async (email: string) => {
  const response = await fetchAPI<{ message: string }>('/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
    skipAuth: true,
  });

  return {
    success: response.success,
    message: response.message,
  };
};

export const resetPassword = async (
  email: string,
  token: string,
  password: string,
  password_confirmation: string
) => {
  const response = await fetchAPI<{ message: string }>('/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      token,
      password,
      password_confirmation,
    }),
    skipAuth: true,
  });

  return {
    success: response.success,
    message: response.message,
  };
};

export const clear2FA = async () => {
  try {
    const res = await fetch('/api/auth/2fa/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error('Failed to clear 2FA state');
    }

    return { success: true };
  } catch (error) {
    console.error('Error clearing 2FA:', error);
    throw error;
  }
};

export const verify2FA = async (code: string) => {
  const res = await fetch('/api/auth/2fa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: '2FA verification failed' }));
    throw new Error(errorData.error || '2FA verification failed');
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  const user: JwtUser = {
    user: {
      id: Number(decodedToken.sub),
      first_name: decodedToken.user.first_name,
      last_name: decodedToken.user.last_name,
      email: decodedToken.user.email,
    },
    permissions: decodedToken.permissions,
    roles: decodedToken.roles,
    organisations: decodedToken.organisations,
    accessible_app: decodedToken.accessible_app,
    teams: decodedToken.teams,
    has_completed_nps: decodedToken.has_completed_nps,
  };

  useAuthStore.getState().setAccessToken(data.access_token);
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setUser(user);

  return data;
};

export const clearAuthCookies = async () => {
  const res = await fetch('/api/auth/clear-auth-cookies', {
    method: 'POST',
  });

  if (!res.ok) {
    throw new Error('Failed to clear auth cookies');
  }

  return { success: true };
};
